'use client';

import { EnglishIqCompleting } from './completion';
import type { EnglishIqMessagesType } from './english-iq-messages-type';
import { EnglishIqStartCard } from './english-iq-start-card';
import type { useEnglishIqWizard } from './hooks';
import { EnglishIqMilestone } from './milestone';
import { EnglishIqQuestion } from './question';
import { EnglishIqResult } from './result';

interface IEnglishIqWizardContentProps {
  locale: string;
  messages: EnglishIqMessagesType;
  requestStart: () => void;
  wizard: ReturnType<typeof useEnglishIqWizard>;
}

function getStartError(error: string | null, messages: EnglishIqMessagesType['wizard']) {
  if (error === 'session') {
    return {
      actionLabel: messages.restart,
      body: messages.sessionExpiredBody,
      title: messages.sessionExpiredTitle,
    };
  }
  if (error === 'start') {
    return { actionLabel: messages.retry, body: messages.errorBody, title: messages.errorTitle };
  }
  return null;
}

export function EnglishIqWizardContent({
  locale,
  messages,
  requestStart,
  wizard,
}: IEnglishIqWizardContentProps): React.JSX.Element {
  if (!wizard.isHydrated) {
    return (
      <div className="grid min-h-[50svh] place-items-center py-10" role="status">
        <p className="text-sm font-medium text-muted-foreground">{messages.wizard.preparing}</p>
      </div>
    );
  }
  if (wizard.result) {
    return (
      <EnglishIqResult
        locale={locale}
        messages={messages}
        onRetake={wizard.reset}
        result={wizard.result}
      />
    );
  }
  if (wizard.completingToken) {
    return (
      <EnglishIqCompleting
        hasError={wizard.error === 'finish'}
        isBusy={wizard.isBusy}
        messages={messages.wizard}
        onRetry={() => wizard.finish(wizard.completingToken ?? undefined)}
      />
    );
  }
  if (wizard.milestone) {
    return (
      <EnglishIqMilestone
        isBusy={wizard.isBusy}
        messages={messages.milestone}
        milestone={wizard.milestone}
        onContinue={wizard.clearMilestone}
        onFinish={() => wizard.finish(wizard.session?.token)}
      />
    );
  }
  if (wizard.session) {
    return (
      <EnglishIqQuestion
        key={wizard.session.item.id}
        index={wizard.session.index}
        isBusy={wizard.isBusy}
        item={wizard.session.item}
        messages={messages.wizard}
        onAnswer={wizard.answer}
        startedAt={wizard.session.startedAt}
      />
    );
  }
  const startError = getStartError(wizard.error, messages.wizard);
  return (
    <EnglishIqStartCard
      actionLabel={startError?.actionLabel ?? messages.hero.cta}
      errorBody={startError?.body ?? null}
      errorTitle={startError?.title ?? null}
      isBusy={wizard.isBusy}
      messages={messages}
      onStart={requestStart}
    />
  );
}
